import { Button } from '../button/button.component';
import { Modal } from '../modal/modal.component';
import css from './overlay.module.less';
import { type Overlay as OverlayModel } from './overlay.service';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import c from 'classnames';
import _ from 'lodash';
import React, { cloneElement, isValidElement, useState, type ReactNode } from 'react';

const ErrorIcon = <FontAwesomeIcon className={css.error} fixedWidth icon={faExclamationTriangle} />;
const WarningIcon = <FontAwesomeIcon className={css.warning} fixedWidth icon={faExclamationTriangle} />;

// Can't use forwardRef due to generic type
export function Overlay<T>(props: OverlayModel<T> & { modalRef?: React.Ref<HTMLDivElement> }) {
    const {
        icon,
        title,
        message,
        actions,
        variant = 'default',
        size,
        canClose,
        showCloseButton,
        onClose,
        close,
        className,
        messageClassName,
        'data-testid': testId,
        divProps,
        modalRef,
    } = props;

    const [inProgress, setInProgress] = useState<ReactNode>();

    function handleClose(result?: T) {
        onClose?.();
        close?.(result);
    }

    async function execute(label: ReactNode, action: (() => T | void) | undefined) {
        let result;

        try {
            setInProgress(label);
            result = await action?.();
        } finally {
            setInProgress(undefined);
            handleClose(result as T | undefined);
        }
    }

    const showClose = showCloseButton && canClose;
    const showHeader = icon || title;

    return (
        <Modal
            data-testid={testId}
            divProps={divProps}
            blocking
            className={c(css[variant], size ? css[size] : '', css.view, className, {
                [css.hasCloseButton]: showClose,
                [css.hasActions]: !!actions,
            })}
            onClickClose={showClose ? () => handleClose() : undefined}
            onClickBackground={canClose ? () => handleClose() : undefined}
            insetCloseButton={variant === 'default'}
            ref={modalRef}
        >
            <>
                {showHeader && (
                    <div data-testid="overlayTitle" className={c(css.header, { [css.increasedMargin]: !!message && !actions })}>
                        {icon && (
                            <div className={css.icon}>
                                {icon === 'error' ? (
                                    ErrorIcon
                                ) : icon === 'warning' ? (
                                    WarningIcon
                                ) : isValidElement(icon) ? (
                                    icon
                                ) : (
                                    <FontAwesomeIcon fixedWidth icon={icon as IconProp} />
                                )}
                            </div>
                        )}
                        {title && <div className={css.title}>{title}</div>}
                    </div>
                )}
                {message && (
                    <div
                        data-testid="overlayMessage"
                        className={c(css.message, messageClassName, {
                            [css.showHeader]: showHeader,
                        })}
                    >
                        {isValidElement(message) && typeof message.type !== 'string' ? cloneElement(message, _.omit(props, 'id')) : message}
                    </div>
                )}
                {actions && (
                    <div className={css.actions}>
                        {_.map(actions, ({ label, action, className, ...props }, index) => (
                            <Button
                                key={index}
                                data-testid={label === 'Ok' ? 'dismissButton' : `overlayAction-${index}`}
                                {...props}
                                big
                                className={c(className, css.action)}
                                onClick={() => execute(label, action)}
                                inactive={inProgress !== undefined}
                                spinner={inProgress === (label ?? null)}
                            >
                                {label}
                            </Button>
                        ))}
                    </div>
                )}
            </>
        </Modal>
    );
}
